/**
 * <sticky-card-stack> — generic mobile card-stacking behaviour.
 *
 * On mobile (below 750px — must match the media query in
 * blocks/_sticky-card-stack.liquid) direct children marked with
 * [data-stack-card] become position: sticky and dock under each other on
 * scroll, each one leaving only its [data-stack-header] row peeking above
 * the next card. This element only measures; the browser does the pinning.
 *
 * Contract:
 *   [data-stack-card]    a participating card (direct child)
 *   [data-stack-header]  the row that stays visible when the card is covered
 *   [data-stack-spacer]  trailing sibling sized by this element so the last
 *                        card has scroll room to hold its stuck position
 *   CSS vars read:       --stack-clearance, --stack-header-gap,
 *                        --stack-spacer-base
 *   CSS vars written:    --stack-peek-offset, --stack-index (per card)
 *   Active card:         gets the `stack-card--active` class; a bubbling
 *                        `sticky-card-stack:change` event fires on change.
 */
class StickyCardStack extends HTMLElement {
  #controller = null;
  #mediaQuery = null;
  #resizeObserver = null;
  #rafId = null;
  #activeIndex = -1;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#mediaQuery = window.matchMedia('(max-width: 749px)');
    this.#mediaQuery.addEventListener('change', () => this.#refresh(), { signal });

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#refresh());
      this.#resizeObserver.observe(this);
    }

    const onScroll = () => {
      if (this.#rafId !== null) return;

      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = null;
        this.#updateActive();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', () => this.#refresh(), { passive: true, signal });
    window.addEventListener('load', () => this.#refresh(), { once: true, signal });

    this.#refresh();
  }

  disconnectedCallback() {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#controller?.abort();
    this.#controller = null;
    this.#mediaQuery = null;
  }

  // Only cards belonging to THIS stack — a nested <sticky-card-stack> owns
  // its own cards.
  #getCards() {
    return [...this.querySelectorAll('[data-stack-card]')].filter(
      (card) => card.closest('sticky-card-stack') === this
    );
  }

  #getSpacer() {
    return this.querySelector(':scope > [data-stack-spacer]');
  }

  #refresh() {
    if (this.#mediaQuery?.matches) {
      this.#layout();
    } else {
      this.#teardown();
    }

    this.#updateActive();
  }

  // Each card docks lower than the previous one by that card's measured
  // header height plus --stack-header-gap, so only header rows stay visible.
  #layout() {
    const cards = this.#getCards();

    if (cards.length === 0) return;

    const style = getComputedStyle(this);
    const readNumberProperty = (property, fallback) => {
      const value = Number.parseFloat(style.getPropertyValue(property));
      return Number.isFinite(value) ? value : fallback;
    };
    const clearance = readNumberProperty('--stack-clearance', 0);
    const headerGap = readNumberProperty('--stack-header-gap', 12);
    const spacerBase = readNumberProperty('--stack-spacer-base', 220);

    let cumulativeOffset = 0;
    const entries = [];

    for (const [index, card] of cards.entries()) {
      card.style.setProperty('--stack-peek-offset', `${cumulativeOffset}px`);
      card.style.setProperty('--stack-index', String(index));

      entries.push({ top: clearance + cumulativeOffset, height: card.offsetHeight });

      if (index < cards.length - 1) {
        const header = card.querySelector('[data-stack-header]');
        cumulativeOffset += (header?.offsetHeight ?? 0) + headerGap;
      }
    }

    // Shorter cards end higher than the tallest one; the deficit is extra
    // scroll distance the spacer must provide so every card actually docks
    // instead of scrolling past its sticky offset.
    const maxBottom = Math.max(...entries.map((entry) => entry.top + entry.height));
    const totalDeficit = entries.reduce((sum, entry) => {
      const deficit = maxBottom - entry.top - entry.height;
      return deficit > 0.5 ? sum + deficit : sum;
    }, 0);
    const viewportSlack = Math.max(0, window.innerHeight - maxBottom);
    const requiredDeficit = Math.max(0, totalDeficit - viewportSlack);

    this.#getSpacer()?.style.setProperty('height', `${spacerBase + requiredDeficit}px`);
  }

  #teardown() {
    for (const card of this.#getCards()) {
      card.style.removeProperty('--stack-peek-offset');
      card.style.removeProperty('--stack-index');
      card.classList.remove('stack-card--active');
    }

    this.#getSpacer()?.style.removeProperty('height');
    this.#activeIndex = -1;
  }

  // A card is active once it has docked at its sticky offset (it's the
  // topmost fully visible card); it stays active until the next card docks
  // over it.
  #updateActive() {
    if (!this.#mediaQuery?.matches) return;

    const cards = this.#getCards();

    if (cards.length === 0) return;

    let nextIndex = 0;

    for (const [index, card] of cards.entries()) {
      const stickyTop = Number.parseFloat(getComputedStyle(card).top) || 0;

      if (card.getBoundingClientRect().top <= stickyTop + 1) {
        nextIndex = index;
      }
    }

    if (nextIndex === this.#activeIndex) return;

    this.#activeIndex = nextIndex;

    for (const [index, card] of cards.entries()) {
      card.classList.toggle('stack-card--active', index === nextIndex);
    }

    this.dispatchEvent(
      new CustomEvent('sticky-card-stack:change', {
        bubbles: true,
        detail: { index: nextIndex, card: cards[nextIndex] },
      })
    );
  }
}

if (!customElements.get('sticky-card-stack')) {
  customElements.define('sticky-card-stack', StickyCardStack);
}
